import React, { useState, useEffect } from 'react';
import { Menu, X, ChevronDown } from 'lucide-react';
import WhatsAppIcon from './icons/WhatsAppIcon';

const navItems = [
  { id: 'home', label: 'الرئيسية' },
  { id: 'packages', label: 'الباقات' },
  { id: 'seasonal', label: 'الباقات الموسمية' },
  { id: 'why-us', label: 'لماذا نحن' },
  { id: 'buses', label: 'باصاتنا' },
  { id: 'hotels', label: 'الفنادق' },
  { id: 'umrah-guide', label: 'دليل العمرة' },
  { id: 'faq', label: 'الأسئلة الشائعة' },
];

export default function Header() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [activeSection, setActiveSection] = useState('home');

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
      const sections = navItems.map(item => item.id);
      for (let i = sections.length - 1; i >= 0; i--) {
        const element = document.getElementById(sections[i]);
        if (element) {
          const rect = element.getBoundingClientRect();
          if (rect.top <= 150) {
            setActiveSection(sections[i]);
            break;
          }
        }
      }
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToSection = (id: string) => {
    document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });
    setIsMobileMenuOpen(false);
  };

  return (
    <>
      <header className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${isScrolled ? 'bg-white/95 backdrop-blur-md shadow-lg py-3' : 'bg-transparent py-4'}`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between">
            {/* Logo */}
            <div className="flex items-center gap-3 cursor-pointer" onClick={() => scrollToSection('home')}>
              <img src="/logo.jpeg" alt="سدرة المنتهى - حملات عمرة من الرياض" className="w-12 h-12 rounded-xl object-cover" loading="eager" />
              <div className="hidden sm:block">
                <span className={`font-amiri text-lg font-bold transition-colors ${isScrolled ? 'text-brown-800' : 'text-white'}`}>سدرة المنتهى</span>
                <p className={`text-xs font-cairo transition-colors ${isScrolled ? 'text-gray-500' : 'text-white/70'}`}>لخدمات المعتمرين</p>
              </div>
            </div>

            {/* Desktop Navigation */}
            <nav className="hidden lg:flex items-center gap-1">
              {navItems.map((item) => (
                <button
                  key={item.id}
                  onClick={() => scrollToSection(item.id)}
                  className={`px-4 py-2 rounded-lg text-sm font-cairo font-medium transition-all ${
                    activeSection === item.id
                      ? isScrolled ? 'bg-brown-50 text-brown-700' : 'bg-white/20 text-white'
                      : isScrolled ? 'text-gray-600 hover:text-brown-700 hover:bg-brown-50' : 'text-white/80 hover:text-white hover:bg-white/10'
                  }`}
                >
                  {item.label}
                </button>
              ))}
            </nav>

            {/* CTA */}
            <div className="flex items-center gap-3">
              <a
                href="https://wa.me/966575179602?text=السلام عليكم، أريد الاستفسار عن خدمات العمرة"
                target="_blank"
                rel="noopener noreferrer"
                className="bg-brown-600 hover:bg-brown-700 text-white px-5 py-2.5 rounded-xl text-sm font-cairo font-bold flex items-center gap-2 transition-all shadow-lg hover:shadow-xl"
              >
                <WhatsAppIcon size={16} />
                <span>احجز الآن</span>
              </a>
              <button
                onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                className={`lg:hidden p-2 rounded-lg transition-colors ${isScrolled ? 'text-gray-700 hover:bg-gray-100' : 'text-white hover:bg-white/10'}`}
              >
                {isMobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* Mobile Menu */}
      <div className={`fixed inset-0 z-40 lg:hidden transition-all duration-300 ${isMobileMenuOpen ? 'opacity-100 visible' : 'opacity-0 invisible'}`}>
        <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={() => setIsMobileMenuOpen(false)} />
        <div className={`absolute top-0 right-0 h-full w-72 bg-white shadow-2xl transition-transform duration-300 ${isMobileMenuOpen ? 'translate-x-0' : 'translate-x-full'}`}>
          <div className="p-6">
            <div className="flex items-center gap-3 mb-8 pb-6 border-b border-gray-100">
              <img src="/logo.jpeg" alt="سدرة المنتهى - حملات عمرة من الرياض" className="w-12 h-12 rounded-xl object-cover" loading="eager" />
              <div>
                <h2 className="text-brown-800 font-amiri text-lg font-bold">سدرة المنتهى</h2>
                <p className="text-gray-500 text-xs font-cairo">لخدمات المعتمرين</p>
              </div>
            </div>
            <nav className="space-y-2">
              {navItems.map((item) => (
                <button
                  key={item.id}
                  onClick={() => scrollToSection(item.id)}
                  className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl font-cairo font-medium transition-all text-right ${
                    activeSection === item.id
                      ? 'bg-brown-50 text-brown-700'
                      : 'text-gray-600 hover:bg-gray-50'
                  }`}
                >
                  {item.label}
                </button>
              ))}
            </nav>
            <div className="mt-8 pt-6 border-t border-gray-100 space-y-3">
              <a
                href="https://wa.me/966575179602?text=السلام عليكم، أريد الاستفسار عن خدمات العمرة"
                target="_blank"
                rel="noopener noreferrer"
                className="w-full bg-brown-600 hover:bg-brown-700 text-white py-3 rounded-xl font-cairo font-bold flex items-center justify-center gap-2"
              >
                <WhatsAppIcon size={18} />
                احجز عبر واتساب
              </a>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
